import React, { useState, useEffect } from 'react';
import { AuditEvent } from './NOCDashboard';
import { NOC_API_ENDPOINTS, nocApiRequest } from '../config/api';

interface NOCEventsFeedProps {
  onUserClick?: (userId: string) => void;
}

const NOCEventsFeed: React.FC<NOCEventsFeedProps> = ({ onUserClick }) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [paused, setPaused] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<string>('');
  const [blockingUser, setBlockingUser] = useState<string | null>(null);

  // Fetch real events from API
  const fetchEvents = async () => {
    try {
      const url = `${NOC_API_ENDPOINTS.events}?limit=50`;
      const data = await nocApiRequest(url);

      if (data.events && data.events.length > 0) {
        setEvents(data.events);
        // Track the most recent event timestamp for polling new events
        if (data.events[0]?.timestamp) {
          setLastFetchTime(data.events[0].timestamp);
        }
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  // Fetch events on mount and poll every 5 seconds (if not paused)
  useEffect(() => {
    if (paused) return;

    fetchEvents(); // Initial fetch
    const interval = setInterval(fetchEvents, 5000);
    return () => clearInterval(interval);
  }, [paused]);

  const handleBlockUser = async (userId: string, reason: string) => {
    if (!window.confirm(`Block user ${userId}?\n\nReason: ${reason}`)) {
      return;
    }

    setBlockingUser(userId);
    try {
      await nocApiRequest(NOC_API_ENDPOINTS.blockUser(userId), {
        method: 'PUT',
        body: JSON.stringify({
          reason,
          operator: 'operator_demo'
        })
      });

      window.alert(`User ${userId} has been blocked successfully.`);
      await fetchEvents(); // Refresh events
    } catch (error) {
      console.error('Failed to block user:', error);
      window.alert('Failed to block user. Please try again.');
    } finally {
      setBlockingUser(null);
    }
  };

  const getClassificationColor = (classification?: string) => {
    switch (classification) {
      case 'SAFE': return 'text-green-400';
      case 'SUSPICIOUS': return 'text-yellow-400';
      case 'ABUSIVE': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getClassificationBg = (classification?: string) => {
    switch (classification) {
      case 'SAFE': return 'bg-green-900/30 border-green-700';
      case 'SUSPICIOUS': return 'bg-yellow-900/30 border-yellow-700';
      case 'ABUSIVE': return 'bg-red-900/30 border-red-700';
      default: return 'bg-gray-900/30 border-gray-700';
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-750 border-b border-gray-700">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <h3 className="text-sm font-semibold text-white">Live Events Feed</h3>
          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-900 text-blue-300 rounded">
            {events.length} events
          </span>
        </div>
        <button
          onClick={() => setPaused(!paused)}
          className={`flex items-center px-3 py-1 text-xs rounded transition-colors ${
            paused
              ? 'bg-green-900/50 text-green-300 hover:bg-green-900/70'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          {paused ? (
            <>
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Resume
            </>
          ) : (
            <>
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Pause
            </>
          )}
        </button>
      </div>

      {/* Events List */}
      <div className="h-96 overflow-y-auto">
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="text-sm">Waiting for events...</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {events.map((event) => (
              <div
                key={event.request_id}
                className={`px-4 py-3 hover:bg-gray-750 transition-colors border-l-2 ${
                  event.blocked ? 'border-red-500' : 'border-transparent'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <button
                        onClick={() => onUserClick && onUserClick(event.user_id)}
                        className="text-xs font-mono text-blue-400 hover:text-blue-300 hover:underline cursor-pointer"
                      >
                        {event.user_id}
                      </button>
                      <span className={`px-2 py-0.5 text-xs rounded border ${getClassificationBg(event.classification)}`}>
                        <span className={getClassificationColor(event.classification)}>
                          {event.classification || 'UNKNOWN'}
                        </span>
                      </span>
                      {event.blocked && (
                        <span className="px-2 py-0.5 text-xs bg-red-900 text-red-300 rounded border border-red-700">
                          BLOCKED
                        </span>
                      )}
                      {(event.risk_score && event.risk_score > 70) || event.blocked ? (
                        <button
                          onClick={() => handleBlockUser(event.user_id, event.block_reason || 'High risk activity detected')}
                          disabled={blockingUser === event.user_id}
                          className="px-2 py-0.5 text-xs bg-red-900/50 text-red-300 rounded border border-red-700 hover:bg-red-900 disabled:opacity-50 transition-colors"
                        >
                          {blockingUser === event.user_id ? 'Blocking...' : '🚫 Block User'}
                        </button>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      {event.interaction_type} • {event.topic}
                    </p>
                    {event.blocked && event.block_reason && (
                      <p className="mt-1 text-xs text-red-400">⚠️ {event.block_reason}</p>
                    )}
                    {event.risk_score !== undefined && (
                      <div className="mt-2 flex items-center">
                        <div className="flex-1 max-w-xs h-1.5 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              event.risk_score > 70
                                ? 'bg-red-500'
                                : event.risk_score > 40
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${event.risk_score}%` }}
                          />
                        </div>
                        <span className="ml-2 text-xs text-gray-500">{event.risk_score}</span>
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex-shrink-0 text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </p>
                    <p className="text-xs text-gray-600 font-mono mt-1">
                      {event.ip_address}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NOCEventsFeed;
